import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { authApi, profileApi, editionApi, productApi } from '../services/api';
import { extractErrorMessage } from '../components/SafeError';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [hasEdition, setHasEdition] = useState(false);
  const [userProducts, setUserProducts] = useState([]);
  const [hasCoreAccess, setHasCoreAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const { data: userData } = await authApi.getMe();
      setUser(userData);

      // Check profile
      const { data: profileData } = await profileApi.get();
      setProfile(profileData.profile);

      // Check edition access - founders always have access
      if (userData.role === 'founder') {
        setHasEdition(true);
        setHasCoreAccess(true);
        setUserProducts(userData.products || ['core']);
      } else {
        const { data: editionData } = await editionApi.checkAccess();
        setHasEdition(editionData.has_edition);
        
        // Check product access
        const { data: productData } = await productApi.getUserProducts();
        setUserProducts(productData.products);
        setHasCoreAccess(productData.has_core_access);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Activity heartbeat - ping every 60 seconds while logged in
  const heartbeatRef = useRef(null);
  
  useEffect(() => {
    if (user) {
      // Start heartbeat
      const sendHeartbeat = () => {
        api.post('/user/heartbeat').catch(() => {}); // Silent fail
      };
      
      // Send immediately on login/mount
      sendHeartbeat();
      
      // Then every 60 seconds
      heartbeatRef.current = setInterval(sendHeartbeat, 60000);
      
      return () => {
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
        }
      };
    }
  }, [user]);

  const login = async (email, password) => {
    try {
      setError(null);
      const { data } = await authApi.login({ email, password });
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);

      // Check profile
      const { data: profileData } = await profileApi.get();
      setProfile(profileData.profile);

      // Check edition access - founders always have access
      if (data.user.role === 'founder') {
        setHasEdition(true);
        setHasCoreAccess(true);
        setUserProducts(data.user.products || ['core']);
      } else {
        const { data: editionData } = await editionApi.checkAccess();
        setHasEdition(editionData.has_edition);
        
        // Check product access
        const { data: productData } = await productApi.getUserProducts();
        setUserProducts(productData.products);
        setHasCoreAccess(productData.has_core_access);
      }

      return data.user;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = extractErrorMessage(detail) || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email, password) => {
    try {
      setError(null);
      const { data } = await authApi.register({ email, password });
      return data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = extractErrorMessage(detail) || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setProfile(null);
      setHasEdition(false);
      setUserProducts([]);
      setHasCoreAccess(false);
    }
  };

  const refreshUser = async () => {
    try {
      const { data: userData } = await authApi.getMe();
      setUser(userData);
      
      const { data: profileData } = await profileApi.get();
      setProfile(profileData.profile);
      
      // Check edition access - founders always have access
      if (userData.role === 'founder') {
        setHasEdition(true);
        setHasCoreAccess(true);
        setUserProducts(userData.products || ['core']);
      } else {
        const { data: editionData } = await editionApi.checkAccess();
        setHasEdition(editionData.has_edition);
        
        // Check product access
        const { data: productData } = await productApi.getUserProducts();
        setUserProducts(productData.products);
        setHasCoreAccess(productData.has_core_access);
      }
    } catch (err) {
      console.error('Refresh user failed:', err);
    }
  };

  const value = {
    user,
    profile,
    hasEdition,
    userProducts,
    hasCoreAccess,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    setProfile,
    setHasEdition,
    setHasCoreAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
